import { reactive, watch, WatchSource } from 'vue'
import { defu } from 'defu'
import { AxiosError } from 'axios'

type FetchOptions = {
  immediate: boolean
  onError: (error: Error) => any
  onFinally: () => any
  watch: WatchSource[]
}

type FetchState = {
  error: Error | null | AxiosError
  pending: boolean
  timestamp: number
}
type FetchHandler = {
  (): Promise<unknown>
  state: FetchState
  refresh: () => Promise<unknown>
}

type FetchFunction = (fn: () => unknown | Promise<unknown>, options?: Partial<FetchOptions>) => FetchHandler
type UseFetchData = FetchFunction & {
  immediate: FetchFunction
}
/**
 * Wrapper to reduce try/catch boilerplate
 */
const useFetchData: UseFetchData = (fn, options) => {
  const config = defu(options, {
    immediate: false,
    onError: console.error,
  })

  const state = reactive({
    pending: false,
    error: null as Error | null,
    timestamp: 0,
  })
  let promise: Promise<unknown> | unknown | null = null
  const doFetch: FetchHandler = async () => {
    state.pending = true
    state.timestamp = Date.now()
    try {
      promise = promise ?? fn()
      const result = await promise
      state.error = null
      return result
    } catch (error) {
      state.error = error as Error
      config.onError?.(error as Error)
    } finally {
      promise = null
      state.pending = false
      config.onFinally?.()
    }
  }
  if (config.immediate) {
    doFetch()
  }
  if (config.watch) {
    watch(config.watch, doFetch)
  }

  doFetch.state = state
  doFetch.refresh = doFetch
  return doFetch
}

/**
 * Version of useFetchData that always sets immediate to true
 */
useFetchData.immediate = (fn, options) => useFetchData(fn, { ...options, immediate: true })

/**
 * Helper function for timestamp check
 */
export async function refreshAfterMinutes(minutes: number, fetch: FetchHandler) {
  if (fetch.state.pending) {
    return
  }
  if (Date.now() - fetch.state.timestamp < minutes * 60 * 1000) {
    return
  }
  await fetch.refresh()
}

export default useFetchData
